import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfMonth, endOfMonth, subMonths, differenceInDays } from "date-fns";
import { fi } from "date-fns/locale";
import { formatCurrency } from "@/lib/formatters";
import { ORDER_STATUS_LABELS, ORDER_STATUS_COLORS } from "@/config/constants";
import { Calendar as CalendarIcon, Download, FileText, Loader2, TrendingUp, Package, Users, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend, Tooltip } from "recharts";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Status labels and colors are now imported from @/config/constants

const statusChartColors: Record<string, string> = {
  draft: "#94a3b8",
  confirmed: "#3b82f6",
  ready_for_installation: "#f59e0b",
  installed: "#22c55e",
  completed: "#10b981",
  cancelled: "#ef4444",
};

export default function Reports() {
  const [startDate, setStartDate] = useState<Date>(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState<Date>(endOfMonth(new Date()));

  // Fetch orders for the date range
  const { data: orders, isLoading: ordersLoading } = useQuery({
    queryKey: ["reports-orders", startDate, endDate],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select(`
          id,
          order_number,
          order_date,
          status,
          total_amount,
          subtotal,
          customer_id,
          cemetery_id,
          partner_id,
          commission_amount,
          cemeteries (name),
          partners (name)
        `)
        .gte("order_date", format(startDate, "yyyy-MM-dd"))
        .lte("order_date", format(endDate, "yyyy-MM-dd"))
        .is("deleted_at", null);

      if (error) throw error;
      return data;
    },
  });

  // Fetch installations for lead time calculation
  const { data: installations, isLoading: installationsLoading } = useQuery({
    queryKey: ["reports-installations", startDate, endDate],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("installations")
        .select(`
          id,
          order_id,
          completed_date,
          installer_id,
          orders (order_date),
          profiles (first_name, last_name)
        `)
        .not("completed_date", "is", null)
        .gte("completed_date", format(startDate, "yyyy-MM-dd"))
        .lte("completed_date", format(endDate, "yyyy-MM-dd"))
        .is("deleted_at", null);

      if (error) throw error;
      return data;
    },
  });

  const isLoading = ordersLoading || installationsLoading;

  // Calculate statistics
  const totalOrders = orders?.length || 0;
  const totalSales = orders?.reduce((sum, o) => sum + (o.total_amount || 0), 0) || 0;
  const averageOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0;

  // Orders by status
  const ordersByStatus = orders?.reduce((acc, order) => {
    acc[order.status] = (acc[order.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  const pieData = Object.entries(ordersByStatus).map(([status, count]) => ({
    name: ORDER_STATUS_LABELS[status] || status,
    value: count,
    color: statusChartColors[status] || "#6b7280",
  }));

  // Sales over time (daily)
  const salesByDay = orders?.reduce((acc, order) => {
    const day = order.order_date;
    if (!acc[day]) {
      acc[day] = { date: day, sales: 0, count: 0 };
    }
    acc[day].sales += order.total_amount || 0;
    acc[day].count += 1;
    return acc;
  }, {} as Record<string, { date: string; sales: number; count: number }>) || {};

  const lineData = Object.values(salesByDay)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(d => ({
      ...d,
      dateLabel: format(new Date(d.date), "d.M", { locale: fi }),
    }));

  // Lead times by installer
  const leadTimesByInstaller = installations?.reduce((acc, inst) => {
    const orderDate = (inst.orders as { order_date: string } | null)?.order_date;
    if (!orderDate || !inst.completed_date) return acc;

    const leadTime = differenceInDays(new Date(inst.completed_date), new Date(orderDate));
    const profiles = inst.profiles as unknown as { first_name: string | null; last_name: string | null } | null;
    const installerName = profiles 
      ? `${profiles.first_name || ""} ${profiles.last_name || ""}`.trim() 
      : "Tuntematon";

    if (!acc[installerName]) {
      acc[installerName] = { total: 0, count: 0 };
    }
    acc[installerName].total += leadTime;
    acc[installerName].count += 1;
    return acc;
  }, {} as Record<string, { total: number; count: number }>) || {};

  const installerLeadTimeData = Object.entries(leadTimesByInstaller).map(([name, data]) => ({
    name,
    avgDays: Math.round(data.total / data.count),
  }));

  const avgLeadTime = installations && installations.length > 0
    ? Math.round(
        installations.reduce((sum, inst) => {
          const orderDate = (inst.orders as { order_date: string } | null)?.order_date;
          if (!orderDate || !inst.completed_date) return sum;
          return sum + differenceInDays(new Date(inst.completed_date), new Date(orderDate));
        }, 0) / installations.length
      )
    : 0;

  // Top 10 cemeteries
  const cemeteryStats = orders?.reduce((acc, order) => {
    if (!order.cemetery_id) return acc;
    const name = (order.cemeteries as { name: string } | null)?.name || "Tuntematon";
    if (!acc[name]) {
      acc[name] = { count: 0, total: 0 };
    }
    acc[name].count += 1;
    acc[name].total += order.total_amount || 0;
    return acc;
  }, {} as Record<string, { count: number; total: number }>) || {};

  const topCemeteries = Object.entries(cemeteryStats)
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, 10);

  // Top 10 partners by commission
  const partnerStats = orders?.reduce((acc, order) => {
    if (!order.partner_id) return acc;
    const name = (order.partners as { name: string } | null)?.name || "Tuntematon";
    if (!acc[name]) {
      acc[name] = { count: 0, commission: 0 };
    }
    acc[name].count += 1;
    acc[name].commission += order.commission_amount || 0;
    return acc;
  }, {} as Record<string, { count: number; commission: number }>) || {};

  const topPartners = Object.entries(partnerStats)
    .sort((a, b) => b[1].commission - a[1].commission)
    .slice(0, 10);

  // Export to Excel
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ["Raportti", `${format(startDate, "d.M.yyyy")} - ${format(endDate, "d.M.yyyy")}`],
      [],
      ["Tilausten lukumäärä", totalOrders],
      ["Kokonaismyynti", `${totalSales.toFixed(2)} €`],
      ["Keskimääräinen tilaus", `${averageOrderValue.toFixed(2)} €`],
      ["Keskimääräinen läpimenoaika", `${avgLeadTime} päivää`],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summarySheet, "Yhteenveto");

    // Orders by status
    const statusData = [["Tila", "Lukumäärä"]];
    Object.entries(ordersByStatus).forEach(([status, count]) => {
      statusData.push([ORDER_STATUS_LABELS[status] || status, count.toString()]);
    });
    const statusSheet = XLSX.utils.aoa_to_sheet(statusData);
    XLSX.utils.book_append_sheet(wb, statusSheet, "Tilat");

    // Top cemeteries
    const cemeteryData = [["Hautausmaa", "Tilauksia", "Myynti €"]];
    topCemeteries.forEach(([name, stats]) => {
      cemeteryData.push([name, stats.count.toString(), stats.total.toFixed(2)]);
    });
    const cemeterySheet = XLSX.utils.aoa_to_sheet(cemeteryData);
    XLSX.utils.book_append_sheet(wb, cemeterySheet, "Hautausmaat");

    // Top partners
    const partnerData = [["Kumppani", "Tilauksia", "Provisio €"]];
    topPartners.forEach(([name, stats]) => {
      partnerData.push([name, stats.count.toString(), stats.commission.toFixed(2)]);
    });
    const partnerSheet = XLSX.utils.aoa_to_sheet(partnerData);
    XLSX.utils.book_append_sheet(wb, partnerSheet, "Kumppanit");

    XLSX.writeFile(wb, `raportti_${format(startDate, "yyyy-MM-dd")}_${format(endDate, "yyyy-MM-dd")}.xlsx`);
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text("Myyntiraportti", 14, 22);
    doc.setFontSize(12);
    doc.text(`${format(startDate, "d.M.yyyy")} - ${format(endDate, "d.M.yyyy")}`, 14, 30);

    // Summary
    doc.setFontSize(14);
    doc.text("Yhteenveto", 14, 45);
    
    autoTable(doc, {
      startY: 50,
      head: [["Mittari", "Arvo"]],
      body: [
        ["Tilausten lukumäärä", totalOrders.toString()],
        ["Kokonaismyynti", `${totalSales.toFixed(2)} €`],
        ["Keskimääräinen tilaus", `${averageOrderValue.toFixed(2)} €`],
        ["Keskimääräinen läpimenoaika", `${avgLeadTime} päivää`],
      ],
    });

    // Orders by status
    const finalY1 = (doc as any).lastAutoTable.finalY;
    doc.setFontSize(14);
    doc.text("Tilaukset tiloittain", 14, finalY1 + 15);

    autoTable(doc, {
      startY: finalY1 + 20,
      head: [["Tila", "Lukumäärä"]],
      body: Object.entries(ordersByStatus).map(([status, count]) => [
        ORDER_STATUS_LABELS[status] || status,
        count.toString(),
      ]),
    });

    // Top cemeteries
    const finalY2 = (doc as any).lastAutoTable.finalY;
    doc.setFontSize(14);
    doc.text("Top 10 hautausmaata", 14, finalY2 + 15);

    autoTable(doc, {
      startY: finalY2 + 20,
      head: [["Hautausmaa", "Tilauksia", "Myynti €"]],
      body: topCemeteries.map(([name, stats]) => [
        name,
        stats.count.toString(),
        stats.total.toFixed(2),
      ]),
    });

    doc.save(`raportti_${format(startDate, "yyyy-MM-dd")}_${format(endDate, "yyyy-MM-dd")}.pdf`);
  };

  // Quick date range buttons
  const setThisMonth = () => {
    setStartDate(startOfMonth(new Date()));
    setEndDate(endOfMonth(new Date()));
  };

  const setLastMonth = () => {
    const lastMonth = subMonths(new Date(), 1);
    setStartDate(startOfMonth(lastMonth));
    setEndDate(endOfMonth(lastMonth));
  };

  const setLast3Months = () => {
    setStartDate(startOfMonth(subMonths(new Date(), 2)));
    setEndDate(endOfMonth(new Date()));
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Raportit</h1>
            <p className="text-muted-foreground mt-1">
              Myynti- ja asennustilastot
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportToExcel}>
              <Download className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button variant="outline" onClick={exportToPDF}>
              <FileText className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>

        {/* Date range filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("justify-start text-left font-normal")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(startDate, "d.M.yyyy", { locale: fi })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => date && setStartDate(date)}
                      locale={fi}
                    />
                  </PopoverContent>
                </Popover>
                <span className="text-muted-foreground">-</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("justify-start text-left font-normal")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(endDate, "d.M.yyyy", { locale: fi })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={(date) => date && setEndDate(date)}
                      locale={fi}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" onClick={setThisMonth}>
                  Tämä kuukausi
                </Button>
                <Button variant="secondary" size="sm" onClick={setLastMonth}>
                  Edellinen kuukausi
                </Button>
                <Button variant="secondary" size="sm" onClick={setLast3Months}>
                  3 kuukautta
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tilauksia</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalOrders}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Kokonaismyynti</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalSales.toFixed(0)} €</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Keskiarvo</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{averageOrderValue.toFixed(0)} €</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Läpimenoaika</CardTitle>
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{avgLeadTime} pv</div>
            </CardContent>
          </Card>
        </div>

        {/* Charts row */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Sales over time */}
          <Card>
            <CardHeader>
              <CardTitle>Myynti aikavälillä</CardTitle>
              <CardDescription>Päivittäinen myynti euroissa</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={lineData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dateLabel" fontSize={12} />
                    <YAxis fontSize={12} />
                    <Tooltip 
                      formatter={(value: number) => [`${value.toFixed(0)} €`, "Myynti"]}
                      labelFormatter={(label) => `Päivä: ${label}`}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="sales" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Orders by status pie chart */}
          <Card>
            <CardHeader>
              <CardTitle>Tilaukset tiloittain</CardTitle>
              <CardDescription>Tilausten jakautuminen</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Lead times by installer */}
        {installerLeadTimeData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Läpimenoajat asentajittain</CardTitle>
              <CardDescription>Keskimääräinen aika tilauksesta asennukseen (päiviä)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={installerLeadTimeData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" fontSize={12} />
                    <YAxis dataKey="name" type="category" fontSize={12} width={120} />
                    <Tooltip formatter={(value: number) => [`${value} päivää`, "Keskiarvo"]} />
                    <Bar dataKey="avgDays" fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top tables */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Top cemeteries */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Top 10 hautausmaata
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {topCemeteries.length === 0 ? (
                  <p className="text-muted-foreground text-sm">Ei dataa valitulla aikavälillä</p>
                ) : (
                  topCemeteries.map(([name, stats], index) => (
                    <div key={name} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-3">
                        <span className="text-muted-foreground font-mono w-6">{index + 1}.</span>
                        <span className="font-medium">{name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">{stats.count} tilausta</div>
                        <div className="text-sm text-muted-foreground">{stats.total.toFixed(0)} €</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top partners */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Top 10 kumppania (provisiot)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {topPartners.length === 0 ? (
                  <p className="text-muted-foreground text-sm">Ei dataa valitulla aikavälillä</p>
                ) : (
                  topPartners.map(([name, stats], index) => (
                    <div key={name} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-3">
                        <span className="text-muted-foreground font-mono w-6">{index + 1}.</span>
                        <span className="font-medium">{name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">{stats.commission.toFixed(0)} € provisio</div>
                        <div className="text-sm text-muted-foreground">{stats.count} tilausta</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
